import { Schema } from 'effect';

/**
 * Document types supported for conversion.
 *
 * Single source of truth - add new formats here.
 * Type is derived automatically from the schema.
 */
export const DocumentTypeSchema = Schema.Literals([
  'html',
  'pdf',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'txt',
  'md',
  'rtf',
  'epub',
  'mobi',
  'email',
  'email_attachment',
]);

export type DocumentType = typeof DocumentTypeSchema.Type;

export const DOCUMENT_TYPE_BY_EXTENSION = {
  pdf: 'pdf',
  doc: 'doc',
  docx: 'docx',
  ppt: 'ppt',
  pptx: 'pptx',
  txt: 'txt',
  md: 'md',
  markdown: 'md',
  mdx: 'md',
  rtf: 'rtf',
  epub: 'epub',
  mobi: 'mobi',
  html: 'html',
  htm: 'html',
  eml: 'email',
} as const satisfies Record<string, DocumentType>;

export const DOCUMENT_TYPE_BY_MIME = {
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'text/x-markdown': 'md',
  'text/mdx': 'md',
  'application/rtf': 'rtf',
  'text/rtf': 'rtf',
  'application/epub+zip': 'epub',
  'application/x-mobipocket-ebook': 'mobi',
  'text/html': 'html',
  'application/xhtml+xml': 'html',
  'message/rfc822': 'email',
} as const satisfies Record<string, DocumentType>;

/**
 * Canonical Content-Type to serve / set for each document type.
 *
 * The reverse direction of detection: given a known `DocumentType`, what HTTP
 * Content-Type do we serve it as (and set on presigned uploads). Exhaustive
 * over `DocumentType` (`satisfies Record<DocumentType, string>`) so adding a
 * new format forces a Content-Type here. Endpoints that serve or upload
 * documents MUST read from this map rather than keep a divergent copy.
 * `email_attachment` has no single canonical media type, so it is served as
 * generic binary.
 */
export const CONTENT_TYPE_BY_DOCUMENT_TYPE = {
  html: 'text/html',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  md: 'text/markdown',
  rtf: 'application/rtf',
  epub: 'application/epub+zip',
  mobi: 'application/x-mobipocket-ebook',
  email: 'message/rfc822',
  email_attachment: 'application/octet-stream',
} as const satisfies Record<DocumentType, string>;

export function documentTypeFromExtension(
  extension: string | undefined,
): DocumentType | undefined {
  if (!extension) return undefined;
  return DOCUMENT_TYPE_BY_EXTENSION[
    extension.toLowerCase() as keyof typeof DOCUMENT_TYPE_BY_EXTENSION
  ];
}

export function documentTypeFromMime(
  mimeType: string | undefined,
): DocumentType | undefined {
  const baseMime = mimeType?.split(';')[0]?.trim().toLowerCase();
  if (!baseMime) return undefined;
  return DOCUMENT_TYPE_BY_MIME[baseMime as keyof typeof DOCUMENT_TYPE_BY_MIME];
}

export function resolveDocumentTypeForFile(
  filename: string,
  mimeType?: string,
): DocumentType | undefined {
  return (
    documentTypeFromExtension(filename.split('.').pop()) ??
    documentTypeFromMime(mimeType)
  );
}

/**
 * Document types a person can bring in as a file: upload, link, Dropbox,
 * Google Drive, Zotero or an email attachment. Email bodies arrive only via
 * the email integration, so `email` and `email_attachment` are excluded. A new
 * `DocumentType` must opt in here to be accepted from users.
 */
export const UploadableDocumentTypeSchema = DocumentTypeSchema.pick([
  'html',
  'pdf',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'txt',
  'md',
  'rtf',
  'epub',
  'mobi',
]);
export type UploadableDocumentType = typeof UploadableDocumentTypeSchema.Type;
export const UPLOADABLE_DOCUMENT_TYPES = UploadableDocumentTypeSchema.literals;
export const isUploadableDocumentType = Schema.is(UploadableDocumentTypeSchema);

const uploadable = (documentType: DocumentType | undefined) =>
  documentType !== undefined && isUploadableDocumentType(documentType)
    ? documentType
    : undefined;

/** The uploadable type of a file, from its name and then its media type. */
export const uploadableDocumentType = (fileName: string, mediaType?: string) =>
  uploadable(resolveDocumentTypeForFile(fileName, mediaType));

export const uploadableDocumentTypeByName = (
  fileName: string,
  mediaType?: string,
) =>
  fileName.lastIndexOf('.') > 0
    ? uploadable(documentTypeFromExtension(fileName.split('.').pop()))
    : uploadable(documentTypeFromMime(mediaType));

/** The uploadable type of a file known only by its media type. */
export const uploadableDocumentTypeFromMime = (mediaType: string) =>
  uploadable(documentTypeFromMime(mediaType));

/**
 * File-picker `accept` map (media type → extensions) for the given types,
 * derived from the detection tables so pickers never keep their own copy.
 */
export const documentTypeAccept = (
  documentTypes: ReadonlyArray<DocumentType>,
) => {
  const accepted = new Set<DocumentType>(documentTypes);
  const extensionsOf = (documentType: DocumentType) =>
    Object.entries(DOCUMENT_TYPE_BY_EXTENSION)
      .filter(([, type]) => type === documentType)
      .map(([extension]) => `.${extension}`);
  return Object.fromEntries(
    Object.entries(DOCUMENT_TYPE_BY_MIME)
      .filter(([, type]) => accepted.has(type))
      .map(([mediaType, type]) => [mediaType, extensionsOf(type)]),
  );
};
